import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Icon, Input, SectionHeading } from '@tviklax/ui-components';
import type { IconName } from '@tviklax/ui-components';
import { useCurrencyFormatter } from '../../../hooks/useCurrencyFormatter';
import {
  computePaymentRails,
  getCheapestRail,
  getNetReceived,
  PACB_CAP_USD,
} from '../utils/paymentRailPricing';
import type { PaymentRailKey, PaymentRailQuote } from '../utils/paymentRailPricing';
import {
  RailRecommendationCard,
  RailInputRow,
  RailGrid,
  RailCard,
  RailBadge,
  RailHeader,
  RailName,
  RailType,
  RailReceiveAmount,
  RailMetaGrid,
  RailMetaItem,
  RailMetaLabel,
  RailMetaValue,
  RailNote,
  RailBlockedNotice,
  FormGroup,
  FormLabel,
} from './styles';

const RAIL_ICON: Record<PaymentRailKey, IconName> = {
  karbon: 'Globe',
  skydo: 'Zap',
  razorpay: 'CreditCard',
  wire: 'Building2',
};

export interface PaymentRailRecommendationProps {
  /** Invoice/shipment value in USD to price rails against. If omitted, an internal editable default is used. */
  initialValueUsd?: number;
  /** Optional label describing where the value came from, e.g. an order id. */
  contextLabel?: string;
}

/**
 * Compares the cost of collecting a USD invoice through the cross-border
 * payment rails available to an Indian exporter — Karbon Forex, Skydo,
 * Razorpay (Export Account) and a traditional bank wire — and highlights the
 * cheapest option, accounting for the RBI PA-CB ~$29,000 per-transaction cap
 * that blocks Skydo/Razorpay above that threshold.
 */
export const PaymentRailRecommendation: React.FC<PaymentRailRecommendationProps> = ({
  initialValueUsd = 15000,
  contextLabel,
}) => {
  const theme = useTheme();
  const { formatCurrency } = useCurrencyFormatter();
  const [valueUsdInput, setValueUsdInput] = useState<string>(String(initialValueUsd));

  const valueUsd = useMemo(() => {
    const parsed = parseFloat(valueUsdInput);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [valueUsdInput]);

  const rails = useMemo<PaymentRailQuote[]>(() => computePaymentRails(valueUsd), [valueUsd]);
  const cheapestRail = useMemo(() => getCheapestRail(rails), [rails]);

  const formatUsd = (amount: number) => formatCurrency(amount, 'USD');

  return (
    <RailRecommendationCard>
      <SectionHeading icon="Globe" size="sm">
        Compare payment collection methods
      </SectionHeading>
      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, marginTop: -8 }}>
        See which rail delivers the most for a cross-border USD invoice{contextLabel ? ` — ${contextLabel}` : ''}, accounting for the RBI PA-CB per-transaction cap.
      </div>

      <RailInputRow>
        <FormGroup style={{ maxWidth: 260 }}>
          <FormLabel>Invoice / shipment value (USD)</FormLabel>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: theme.colors.textSecondary, fontWeight: 600 }}>$</span>
            <Input
              type="number"
              min={0}
              step={100}
              placeholder="15000"
              value={valueUsdInput}
              onChange={(e) => setValueUsdInput(e.target.value)}
              style={{ paddingLeft: 28 }}
            />
          </div>
        </FormGroup>
        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textMuted, paddingBottom: 6 }}>
          RBI PA-CB cap: {formatUsd(PACB_CAP_USD)} per transaction
        </div>
      </RailInputRow>

      {valueUsd <= 0 ? (
        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
          Enter an invoice value to compare payment rails.
        </div>
      ) : (
        <RailGrid>
          {rails.map((rail) => {
            const isRecommended = cheapestRail?.key === rail.key;
            const netReceived = getNetReceived(valueUsd, rail);

            return (
              <RailCard key={rail.key} $recommended={isRecommended} $capped={rail.capped}>
                {isRecommended && (
                  <RailBadge>
                    <Icon name="CheckCircle" size={11} color="#ffffff" />
                    Lowest cost
                  </RailBadge>
                )}

                <RailHeader>
                  <Icon name={RAIL_ICON[rail.key]} size={16} color={theme.colors.primary} />
                  <RailName>{rail.name}</RailName>
                </RailHeader>
                <RailType>{rail.type}</RailType>

                {rail.capped ? (
                  <RailBlockedNotice>
                    <Icon name="Lock" size={13} />
                    <span>
                      Blocked — invoice exceeds the RBI PA-CB cap of {formatUsd(PACB_CAP_USD)}. Use Karbon Forex or a
                      traditional bank wire instead.
                    </span>
                  </RailBlockedNotice>
                ) : (
                  <>
                    <div>
                      <RailMetaLabel>You&apos;d receive</RailMetaLabel>
                      <RailReceiveAmount>{formatUsd(netReceived)}</RailReceiveAmount>
                    </div>

                    <RailMetaGrid>
                      <RailMetaItem>
                        <RailMetaLabel>Fee</RailMetaLabel>
                        <RailMetaValue>{formatUsd(rail.fee)}</RailMetaValue>
                      </RailMetaItem>
                      <RailMetaItem>
                        <RailMetaLabel>FX loss</RailMetaLabel>
                        <RailMetaValue>{rail.fxLoss > 0 ? formatUsd(rail.fxLoss) : 'None'}</RailMetaValue>
                      </RailMetaItem>
                      <RailMetaItem style={{ gridColumn: '1 / -1' }}>
                        <RailMetaLabel>Settlement speed</RailMetaLabel>
                        <RailMetaValue>{rail.speed}</RailMetaValue>
                      </RailMetaItem>
                    </RailMetaGrid>
                  </>
                )}

                <RailNote>{rail.note}</RailNote>
              </RailCard>
            );
          })}
        </RailGrid>
      )}
    </RailRecommendationCard>
  );
};
